/**
 * @module KatapultJobUtilities
 * A module that contains utility functions for the KatapultJob module. Also contains reusable types.
 * These can be used outside of KatapultJob.
 */

/** @import { ClientOrAdminFirebase } from '../types/ClientOrAdminFirebase.js' */

/** @import { PixelSelection } from './KatapultJobTypes.js' */

import firebaseApp from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import { serverTimestamp } from 'firebase/database';
import { FirebaseEncode } from '../FirebaseEncode.js';
import { KatapultGeometry } from 'katapult-toolbox';
import { Path } from '../Path.js';
import { diff } from 'deep-object-diff';

/**
 * Get the Firebase App appropriate for the environment.
 * @returns {ClientOrAdminFirebase} The firebase API.
 */
export function getFirebaseApp() {
  // Prefer the FirebaseWorker API if it exists.
  if (typeof globalThis.FirebaseWorker != 'undefined') return globalThis.FirebaseWorker;
  // Otherwise, use the default Firebase API.
  return firebaseApp;
}

/**
 * Returns a Firebase folder path for a project folder.
 * @param {string} path - The project folder path to encode.
 * @returns {string} The encoded project folder path.
 */
export function getFirebaseFolderPath(path) {
  return path.split('/').map(FirebaseEncode.encode).join('/folders/');
}

/**
 * Get data about the authenticated user making firebase requests.
 * @param {ClientOrAdminFirebase} firebaseSDK - The firebase SDK to use.
 * @returns {Promise<AuthDataResult?>} Data about the authenticated user, null if no user is authenticated or using the Admin SDK.
 */
export async function getAuthData(firebaseSDK) {
  const authService = firebaseSDK.auth();
  const currentUser = 'currentUser' in authService ? authService.currentUser : null;
  // If there is no current user, return null (this is a legitimate result when using the Admin SDK).
  if (!currentUser) return null;

  const token = await currentUser.getIdTokenResult();
  return {
    uid: currentUser.uid,
    email: currentUser.email,
    root_company: token?.claims?.rootCompany,
    user_group: token?.claims?.userGroup
  };
}

/**
 * Get the modification timestamp for an item.
 * @param {ClientOrAdminFirebase} firebaseSDK - The firebase SDK to use.
 * @param {string} [method] - A description for how the modification was made.
 * @returns {Promise<ModificationTimestamp?>} The modification timestamp.
 */
export async function getModificationTimestamp(firebaseSDK, method) {
  const authDataResult = await getAuthData(firebaseSDK);

  return {
    uid: authDataResult?.uid ?? 'unknown',
    timestamp: Date.now(),
    method: method ?? 'unknown'
  };
}

/**
 * Apply a mutation to an existing set of item attributes.
 * @param {ItemAttributes} attributes - The existing attributes to mutate.
 * @param {ItemAttributesMutation} attributeMutations - The mutations to apply to the attributes.
 * @returns {ItemAttributesMutation} The mutated attributes.
 */
export function applyItemAttributesMutation(attributes = {}, attributeMutations) {
  const attributesList = Object.keys({ ...attributes, ...attributeMutations });
  return Object.fromEntries(
    attributesList.map((attribute) => [
      attribute,
      attributeMutations[attribute] !== null ? { ...attributes[attribute], ...attributeMutations[attribute] } : null
    ])
  );
}

/**
 * Create an item attributes mutation that replaces any existing attribute values.
 * @param {ItemAttributes} attributes - The existing item attributes.
 * @param {ItemAttributesMutation} baseAttributeMutations - The base mutations to apply to the attributes.
 * @returns {ItemAttributesMutation} The final attribute mutation.
 */
export function createReplacingItemAttributesMutation(attributes = {}, baseAttributeMutations) {
  const mutatingAttributesList = Object.keys(baseAttributeMutations);
  return Object.fromEntries(
    // Make an entry for each attribute that will be updated.
    mutatingAttributesList.map((attribute) => [
      attribute,
      // If the new attribute value is not null, merge the changed attribute values with the existing ones.
      baseAttributeMutations[attribute] !== null
        ? {
            // Set every existing attribute instance to null.
            ...Object.fromEntries(Object.keys(attributes[attribute] ?? {}).map((key) => [key, null])),
            // Merge the new attribute values.
            ...baseAttributeMutations[attribute]
          }
        : null
    ])
  );
}

/**
 * Create an object mutation that replaces all existing object values with either new values or null.
 * @param {Record<string, any>} baseObject - The existing object.
 * @param {Record<string, any>} objectMutations - The new values to apply to the object.
 * @returns {Record<string, any>} The final object mutation.
 */
export function createReplacingObjectMutation(baseObject = {}, objectMutations) {
  const allKeys = Object.keys({ ...baseObject, ...objectMutations });
  return Object.fromEntries(allKeys.map((key) => [key, objectMutations[key] != null ? objectMutations[key] : null]));
}

/**
 * Returns the job-relative path of an item.
 * @param {string} itemId - The id of the item.
 * @param {string} itemType - The group this item belongs to (one of `'node'`, `'connection'`, or `'section'`).
 * @returns {string} The job-relative path of the item.
 */
export function getItemRelativePath(itemId, itemType) {
  switch (itemType) {
    case 'node':
      return `nodes/${itemId}`;
    case 'connection':
      return `connections/${itemId}`;
    case 'section': {
      const [connectionId, sectionKey] = itemId.split(':');
      return `connections/${connectionId}/sections/${sectionKey}`;
    }
  }
  throw new Error(`Unknown itemType: ${itemType}`);
}

/**
 * Returns the job-relative path of an item's attributes collection.
 * @param {string} itemId - The id of the item.
 * @param {string} itemType - The group this item belongs to (one of `'node'`, `'connection'`, or `'section'`).
 * @returns {string} The job-relative path of the item's attributes collection.
 */
export function getItemAttributesRelativePath(itemId, itemType) {
  const itemRelativePath = getItemRelativePath(itemId, itemType);
  switch (itemType) {
    case 'node':
      return `${itemRelativePath}/attributes`;
    case 'connection':
      return `${itemRelativePath}/attributes`;
    case 'section':
      return `${itemRelativePath}/multi_attributes`;
  }
  throw new Error(`Unknown itemType: ${itemType}`);
}

/**
 * Validate a location input.
 * @param {[number, number]} location - The location to validate.
 * @returns {{ latitude: number, longitude: number }} The validated location.
 */
export function validateLocationInput(location) {
  // Validate the function input
  if (!Array.isArray(location) || location.length !== 2) throw new Error('Location must be an array with two elements');

  // Get the latitude and longitude
  const [latitude, longitude] = location;
  // Validate the latitude and longitude
  if (typeof latitude !== 'number' || typeof longitude !== 'number') throw new Error('Invalid latitude or longitude');
  if (latitude < -90 || latitude > 90) throw new Error('Latitude must be between -90 and 90');
  if (longitude < -180 || longitude > 180) throw new Error('Longitude must be between -180 and 180');

  return { latitude, longitude };
}

/**
 * Validate a location input and snap it to a line between two nodes.
 * @param {[number, number]} location - The location to validate and snap.
 * @param {{ latitude: number, longitude: number }} node1 - The first node to snap to.
 * @param {{ latitude: number, longitude: number }} node2 - The second node to snap to.
 * @returns {{ latitude: number, longitude: number }} The snapped location.
 */
export function validateAndSnapLocationInput(location, node1, node2) {
  const { latitude, longitude } = validateLocationInput(location);
  const { latitude: lat1, longitude: lng1 } = node1;
  const { latitude: lat2, longitude: lng2 } = node2;

  const { lat: snappedLat, long: snappedLng } = KatapultGeometry.SnapToLine(latitude, longitude, lat1, lng1, lat2, lng2);
  return { latitude: snappedLat, longitude: snappedLng };
}

/**
 * Rebase a multi-location update object to a new base path.
 * @param {string} newBasePath - The new base path to prepend to every update path.
 * @param {Record<string, any>} update - The update object to rebase.
 * @returns {Record<string, any>} The rebased update object.
 */
export function rebaseUpdatePaths(newBasePath, update) {
  const updated = {};
  for (const path in update) {
    updated[`${newBasePath}/${path}`] = update[path];
  }
  return updated;
}

/**
 * Filter an update object to only include paths that start with a given path. Paths are sliced to remove the filter path.
 * @param {Record<string, any>} update - The update object to filter.
 * @param {string} filterToPath - The path to filter to.
 * @returns {Record<string, any>} The filtered update object.
 */
export function filterUpdateToPath(update, filterToPath) {
  const filterToPathWithSlash = filterToPath.endsWith('/') ? filterToPath : `${filterToPath}/`;
  const sliceIndex = filterToPathWithSlash.length;

  const filteredUpdate = {};
  for (const path in update) {
    if (path.startsWith(filterToPath)) {
      const slicedPath = path.slice(sliceIndex);
      filteredUpdate[slicedPath] = update[path];
    }
  }
  return filteredUpdate;
}

/**
 * Determine if a photo has photofirst data with any non-empty input model groups.
 * @param {object} photo - A photo object from a job
 * @returns {boolean} Whether or not the photo has a photofirst data object with any non-empty input model groups
 */
export function photoHasPhotoFirstData(photo) {
  // Check if the photo has a photofirst data object at all
  const photoFirstData = photo.photofirst_data;
  if (!photoFirstData) return false;

  // Check if the photo has any non-empty input model groups
  for (const inputModelKey in photoFirstData) {
    // Input model keys starting with an underscore don't count
    if (inputModelKey.startsWith('_')) continue;
    const inputModel = photoFirstData[inputModelKey];
    if (inputModel && Object.keys(inputModel).length > 0) return true;
  }

  // If we didn't find any non-empty input model groups, the photo doesn't have any photofirst data
  return false;
}

/**
 * Get the first associated item of a photo.
 * @param {object} photo - A photo object from a job
 * @param {object} jobNodes - A map of node objects from a job
 * @param {object} jobConnections - A map of connection objects from a job
 * @returns {PhotoAssociatedItemRecord?} The first associated item of the photo or null if no associated item was found
 */
export function getPhotoAssociatedItemRecord(photo, jobNodes, jobConnections) {
  const [idAndTypeOfFirstAssociatedItem] = Object.entries(photo?.associated_locations ?? {});
  // If there is no associated item, return null
  if (idAndTypeOfFirstAssociatedItem == null) return null;

  // Get the associated item
  const [itemId, itemType] = idAndTypeOfFirstAssociatedItem;
  const [nodeOrConnectionId, sectionKey] = itemId.split(':');
  const item = itemType === 'node' ? jobNodes[nodeOrConnectionId] : jobConnections[nodeOrConnectionId]?.sections[sectionKey];
  if (!item) return null;

  return { itemId, itemType, item };
}

/**
 * Applies an update object to an existing object. The provided object will be modified in place.
 * @param {object} object - The object to update.
 * @param {Record<string, any>} update - The update object to apply.
 * @param {object} [options]
 * @param {string} [options.filterToPath] - A path to filter the update to. The update will be "rebased" to this path.
 * @param {string} [options.newBasePath] - A new base path to prepend to every update path.
 * @returns {object} A reference to the updated object.
 */
export function applyUpdateToObject(object, update, options = {}) {
  const { filterToPath = null, newBasePath = null } = options;
  if (filterToPath) update = filterUpdateToPath(update, filterToPath);
  if (newBasePath) update = rebaseUpdatePaths(newBasePath, update);

  for (const path in update) {
    const value = update[path];
    Path.setAndDelete(object, path, value, '/');
  }

  return object;
}

/**
 * @param {object} original - The original object.
 * @param {object} updated - The updated object.
 * @returns {Record<string, any>} An update object that would mutate `original` to `updated`.
 */
export function updateObjectFromDiff(original, updated) {
  const diffResult = diff(original, updated);
  const flatDiff = Path.flattenObject(diffResult, '/');

  // Replace undefined values with null, and fix timestamps
  const entries = Object.entries(flatDiff);
  for (const [path, value] of entries) {
    if (value === undefined) flatDiff[path] = null;

    // Ugly exception for firebase timestamps
    if (path.endsWith('/.sv') && value === 'timestamp') {
      flatDiff[path.replace(/\/\.sv$/, '')] = serverTimestamp();
      delete flatDiff[path];
    }
  }

  return flatDiff;
}

/**
 * Gets the name for a photo file based on the photo id, type, and file.
 * @param {string} photoId - The id of the photo.
 * @param {string} photoExtension - The file to get the name for
 * @param {PhotoType} photoType - The type of the photo (full, large, small, etc...)
 */
export function getPhotoFileName(photoId, photoExtension, photoType) {
  if (!photoId || !photoExtension || !photoType)
    throw new Error('Could not get photo file name because one or more parameters are missing');
  // Build the file name
  const fileName = `${photoId}_${photoType}.${photoExtension}`;
  return fileName;
}

/**
 *
 * @param {string|object|null} mapStyles - The map styles for the job (can be either an ID or a map styles object).
 * @param {firebaseApp.database.Reference} jobModelsRef
 * @param {firebaseApp.database.Reference} jobOwnerModelsRef
 * @returns
 */
export async function buildJobStyles(mapStyles, jobModelsRef, jobOwnerModelsRef) {
  // Initialize empty job map styles.
  let jobStyles;

  // Setup a helper function to set the job styles from a set of map styles.
  const setJobStyles = (mapStyles) => {
    // Do nothing if map styles is null.
    if (mapStyles == null) return;
    // Initialize job styles.
    jobStyles = {};
    // Copy map styles to break any references.
    mapStyles = Path.copy(mapStyles);
    // Remove unnecessary keys.
    delete mapStyles._key;
    delete mapStyles._default;
    // Set mapStyles as the default style.
    jobStyles['default'] = mapStyles;
  };

  // Handle provided map style ID.
  if (typeof mapStyles == 'string' && mapStyles != '') {
    mapStyles = mapStyles.toLowerCase();
    const style = await jobOwnerModelsRef
      .child(`map_styles/${mapStyles}`)
      .once('value')
      .then((s) => s.val());
    if (style != null) setJobStyles(style);
  }
  // Handle provided map styles object.
  else if (typeof mapStyles == 'object' && mapStyles != null) setJobStyles(mapStyles);

  // If job styles have not been set yet, use the model or company default.
  if (jobStyles == null) {
    // Fetch model's default map styles.
    const modelDefaultMapStyles = await jobModelsRef
      .child(`config/map/default_map_styles`)
      .once('value')
      .then((s) => s.val());
    // Use model's default map styles if they exist.
    if (modelDefaultMapStyles != null) setJobStyles(modelDefaultMapStyles);
    // Otherwise, use the user's company's default map styles.
    else {
      // Fetch user's company's map styles.
      const companyMapStyles = await jobOwnerModelsRef
        .child(`map_styles`)
        .once('value')
        .then((s) => s.val());
      const companyDefaultMapStyles = Object.values(companyMapStyles ?? {}).find((style) => style._default);
      if (companyDefaultMapStyles != null) setJobStyles(companyDefaultMapStyles);
    }
  }

  // Make sure jobStyles is at least an empty object.
  jobStyles ??= {};

  return jobStyles;
}

/**
 *
 * @param {firebaseApp.database.Reference} jobModelsRef
 * @param {string} jobId
 * @returns
 */
export async function getDefaultSavedViews(jobModelsRef, jobId) {
  const savedViews = {};
  const layers = {};

  const modelDefaultSavedView = await jobModelsRef
    .child('config/map/default_saved_view')
    .once('value')
    .then((s) => s.val());
  if (modelDefaultSavedView != null) {
    const defaultSavedView = await jobModelsRef
      .child(`saved_views/${modelDefaultSavedView}`)
      .once('value')
      .then((s) => s.val());
    if (defaultSavedView != null) {
      defaultSavedView.default = true;
      defaultSavedView.settings ??= {};
      defaultSavedView.settings.multiJobIds ??= {};
      defaultSavedView.settings.multiJobIds[jobId] = { url: `photoheight/jobs/${jobId}/geohash` };
      if (defaultSavedView.layers) {
        layers.list = defaultSavedView.layers;
        delete defaultSavedView.layers;
      }
      savedViews[jobModelsRef.push().key] = defaultSavedView;
    }
  }

  return { savedViews, layers };
}

// Reusable types

/**
 * @typedef {Object} AuthDataResult Data about the authenticated user making firebase requests.
 * @property {string} uid - The user's unique identifier.
 * @property {string?} email - The user's email address.
 * @property {string|undefined} root_company - The user's root company.
 * @property {string|undefined} user_group - The user's active company.
 */

/**
 * @typedef {Object} ModificationTimestamp The modification timestamp for an item.
 * @property {string} uid - The user's unique identifier.
 * @property {number} timestamp - The timestamp of the modification.
 * @property {string} method - A description for how the modification was made.
 */

/**
 * @typedef {Object} JobDetails A virtual database object containing details about a job.
 * @property {string} name - The job's name.
 * @property {string} model - The job's model.
 * @property {string} owner - The job's owner.
 * @property {string} projectFolder - The job's project folder.
 * @property {string} [projectId] - The job's project id.
 * @property {{ published?: boolean, [key: string]: any }} [status] - The job's status object.
 * @property {boolean} [done] - The job's "done" state.
 */

/**
 * @typedef {Object} CreateNodeData
 * @property {string} _key - The key to use for the node (if not provided, a random push ID will be used).
 * @property {string} _create_method - The method or tool used to create the node.
 * @property {ItemAttributes} attributes - The attributes to set on the node.
 * @property {Record<string, { association: AssociationValue }>} photos - The photos to associate with the node.
 * @property {string} button - The button used to create the node.
 * @property {string} companyLookupId - The utility company's unique identifier for this node in a portal.
 * @property {{ rotation: number }} style_adjustments - The style adjustments to apply to the node.
 */

/**
 * @typedef {Object} CreateConnectionData
 * @property {string} _key - The key to use for the connection (if not provided, a random push ID will be used).
 * @property {string} _create_method - The method or tool used to create the connection.
 * @property {ItemAttributes} attributes - The attributes to set on the connection.
 * @property {Record<string, object>} sections - The sections to create on the connection.
 * @property {Record<string, { association: AssociationValue }>} photos - The photos to associate with the connection.
 */

/**
 * @typedef {Object} CreateSectionData
 * @property {string} _key - The key to use for the section (if not provided, a random push ID will be used).
 * @property {string} _create_method - The method or tool used to create the section.
 * @property {ItemAttributes} multi_attributes - The attributes to set on the section.
 * @property {Record<string, { association: AssociationValue }>} photos - The photos to associate with the section.
 */

/**
 * @typedef {Object} PhotoElementCreateParams
 * @property {string} [id] - The id of the element (if not provided, a random push ID will be used).
 * @property {string} element_type - The type of the element (e.g. 'wire', 'insulator', 'arm', etc.).
 * @property {?PixelSelection} [pixel_selection] - The pixel selection for the element (omit for "chip" elements).
 * @property {?string} [manual_height] - Manually-set height of the element in feet-inches.
 * @property {Record<string, any>} [attributes] - Flat map of attributes to set (or unset) on the element.
 * @property {?string} [parent_id] - Id of an existing element to attach this element to. If omitted, the element will be a root element.
 * @property {?string} [trace_id] - Id of the trace this element belongs to.
 */

/**
 * @typedef {Omit<PhotoElementCreateParams, 'id' | 'element_type'>} PhotoElementUpdateParams
 */

/**
 * @typedef {Object} UpdateAttributesOptions
 * @property {boolean} [replaceExisting=false] - Whether to replace the attribute(s) existing values; any instances that are not present
 * in the new attribute values object will be removed.
 */

/**
 * @typedef {Object} PhotoAssociation
 * @property {string} photoId - The id of the photo.
 * @property {string} itemId - The id of the item.
 * @property {string} itemType - The type of this item (one of `'node'`, `'connection'`, or `'section'`).
 */

/**
 * @typedef {Object} PhotoAssociatedItemRecord
 * @property {string} itemId - The id of the associated item.
 * @property {string} itemType - The type of the associated item (one of `'node'` or `'connection'`).
 * @property {object} item - The associated item.
 */

/**
 * @typedef {Record<string, any>|undefined} MetadataAttributes
 * @typedef {Record<string, any>} MetadataAttributesMutation
 * @typedef {Record<string, Record<string, any>>|undefined} ItemAttributes
 * @typedef {Record<string, Record<string, any>|null>} ItemAttributesMutation
 */

/**
 * @typedef {'full' | 'extra_large' | 'large' | 'small' | 'tiny' | 'exif'} PhotoType
 */

/**
 * @typedef {'main'|true|null} AssociationValue
 */
